import { useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Link } from "react-router-dom";
import AlunoFindDialog from "../Aluno/AlunoFindDialog";
import MuralFotoLightbox from "../Mural/MuralFoto/MuralFotoLightbox";
import { findByAlunoCodigo } from "./api-aluno-foto";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import Container from "../UI/Container";
import Loading from "../UI/Loading";
import AlunoFotoList from "./AlunoFotoList";

const emptyBoxSx = {
  py: 6,
  textAlign: "center",
  color: "text.secondary",
  border: 1,
  borderStyle: "dashed",
  borderColor: "divider",
  borderRadius: 2,
};

const AlunoFotoContainer = () => {
  const [aluno, setAluno] = useState(null);
  const [listaAF, setListaAF] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [fotoIndex, setFotoIndex] = useState(null);
  const codAluno = aluno?.codigo;

  useEffect(() => {
    setListaAF([]);
    if (!codAluno) return undefined;
    let active = true;
    setIsLoading(true);
    findByAlunoCodigo(codAluno)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else if (Array.isArray(data)) {
          // Mais recentes primeiro.
          setListaAF([...data].sort((a, b) => (b.anoLetivo ?? 0) - (a.anoLetivo ?? 0)));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [codAluno]);

  const comFoto = listaAF.filter((af) => af.foto);
  const fotoAtual = comFoto[0]?.foto;
  // A grade usa a posição na lista completa; o visualizador só recebe fotos com imagem.
  const handleOpenFoto = (index) => setFotoIndex(comFoto.indexOf(listaAF[index]));

  const renderFotos = () => {
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description="Carregando fotos..." />
        </Box>
      );
    }
    if (listaAF.length === 0) {
      return (
        <Box sx={emptyBoxSx}>
          <PhotoLibraryOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1}>
            Nenhuma foto cadastrada para este aluno.
          </Typography>
        </Box>
      );
    }
    return <AlunoFotoList listaAF={listaAF} onOpenFoto={handleOpenFoto} />;
  };

  return (
    <Container>
      <AlunoFindDialog openSearch={openSearch} setOpenSearch={setOpenSearch} setAluno={setAluno} />
      <MuralFotoLightbox
        fotos={comFoto}
        index={fotoIndex}
        onClose={() => setFotoIndex(null)}
        onChangeIndex={setFotoIndex}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h6">Aluno Foto</Typography>
          <Typography variant="body2" color="text.secondary">
            Fotos dos alunos por ano letivo.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AddPhotoAlternateOutlinedIcon />}
          component={Link}
          to="/aluno-foto-insert"
        >
          Inserir por Ano Letivo
        </Button>
      </Stack>

      {aluno ? (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={fotoAtual} alt={aluno.nome} sx={{ width: 56, height: 56 }} />
              <Box flexGrow={1} minWidth={0}>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {aluno.nome}
                </Typography>
                {aluno.codAlunoEscola && (
                  <Typography variant="body2" color="text.secondary">
                    Código {aluno.codAlunoEscola}
                  </Typography>
                )}
              </Box>
              <Button startIcon={<SwapHorizIcon />} onClick={() => setOpenSearch(true)}>
                Trocar Aluno
              </Button>
            </Stack>
          </Paper>
          {renderFotos()}
        </Stack>
      ) : (
        <Box sx={emptyBoxSx}>
          <PersonSearchOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1} mb={2}>
            Selecione um aluno para ver as fotos.
          </Typography>
          <Button variant="contained" startIcon={<PersonSearchOutlinedIcon />} onClick={() => setOpenSearch(true)}>
            Selecionar Aluno
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default AlunoFotoContainer;
